import { action, computed, observable, set } from "mobx";
import Address from "./Address";
import ProviderDefaults from "./Customer/ProviderDefaults";
import Currency from "~/constants/Credit/Currency";
import Model from "~/models/Model";

class Customer extends Model {
  /**
   * @property {number}
   */
  @observable customerId = null;

  /**
   * @property {string}
   */
  @observable firstName = "";

  /**
   * @property {string}
   */
  @observable lastName = "";

  /**
   * @property {string}
   */
  @observable email = "";

  /**
   * @property {string}
   */
  @observable company = "";

  /**
   * @property {string}
   */
  @observable agentLicenseNumber = "";

  /**
   * @property {string}
   */
  @observable contactPhone = "";

  /**
   * @property {string}
   */
  @observable contactPhone2 = "";

  /**
   * @property {string}
   */
  @observable disclaimer = "";

  /**
   * @property {string}
   */
  @observable designation = "";

  /**
   * @property {string}
   */
  @observable logoUrl = "";

  /**
   * @property {string} photo base64 encoded
   */
  @observable photoData = "";

  /**
   * @property {string} photo base64 encoded
   */
  @observable logoData = "";

  /**
   * @property {string}
   */
  @observable photoUrl = "";

  /**
   * @property {string}
   */
  @observable providerId = "";

  /**
   * @property {string}
   */
  @observable title = "";

  /**
   * @property {string}
   */
  @observable website = "";

  /**
   * @property {string[]}
   */
  @observable assistantCcEmails = [];

  /**
   * @property {string}
   */
  @observable canonicalDomain = "";

  /**
   * @property {Address|null}
   */
  @observable address = null;

  /**
   * @property {string|null}
   * Remember this is not the coalesced value
   */
  @observable locale = null;

  /**
   * @property {ProviderDefaults|null}
   */
  @observable providerDefaults = null;

  /**
   * @property {string}
   */
  @observable photoPathWeb = "";

  /**
   * @property {string}
   */
  @observable logoPathWeb = "";

  /**
   * @property {number|null}
   */
  @observable deactivatedDts = null;

  /**
   * @property {number|null}
   */
  @observable uiMeet3dLevel = null;

  /**
   * @property {boolean}
   */
  @observable enableCustomerPortal = true;

  /**
   * @property {number|null}
   */
  @observable defaultCreditCardId = null;

  /**
   * @property {Object}
   */
  @observable uiMeet3dSettings = {};

  /**
   * @property {string|null}
   */
  @observable defaultTimezone = null;

  /**
   * @property {bool}
   */
  @observable hasUrbanimmersiveGroupSpecialPricing = false;

  /**
   * @property {bool}
   */
  @observable isUsingExternalInvoicingForSubscriptions = false;

  /**
   * Set new data to our model
   * @override
   * @param {object} data
   */
  @action
  setData(data) {
    const { address, providerDefaults, ...rest } = data;
    set(this, rest);

    if (address) {
      this.address = new Address(address);
    }

    if (providerDefaults) {
      this.providerDefaults = new ProviderDefaults(providerDefaults);
    }
  }

  @computed
  get defaultTimezoneCoalesced() {
    if (this.defaultTimezone !== null) {
      return this.defaultTimezone;
    }

    return this.providerDefaults.defaultTimezone;
  }

  @computed
  get currency() {
    return this.address && this.address.country === "Canada" ? Currency.CAD : Currency.USD;
  }
}

export default Customer;
